/**
 * Test Lab landing page (T10 — plan §10.2).
 *
 * URL: ``/lab``. Read-only past-runs table that polls
 * ``GET /api/test-runs`` every 2 s so an in-flight ``running`` row
 * transitions to ``passed`` / ``failed`` without a manual reload.
 *
 * The "+ New run" button is a placeholder until the
 * :file:`/lab/new` wizard lands; for now it's disabled with an
 * inline note so the layout doesn't shift when the wizard ships.
 *
 * Disabled state: when the backend returns 404 on
 * ``/api/test-runs`` the store's :prop:`disabled` flag flips and
 * we render an explainer ("Test Lab is not enabled on this
 * server") rather than an error toast — production prod images
 * default the flag to OFF.
 */
import { useEffect } from 'react'
import { Link } from 'react-router-dom'

import { useLabRunStore } from '@/stores/labRunStore'
import { durationSeconds, type LabRunSummary } from '@/types/labRuns'

export function LabPage() {
  const runs = useLabRunStore((s) => s.runs)
  const loading = useLabRunStore((s) => s.loading)
  const disabled = useLabRunStore((s) => s.disabled)
  const error = useLabRunStore((s) => s.error)
  const startPolling = useLabRunStore((s) => s.startPolling)
  const stopPolling = useLabRunStore((s) => s.stopPolling)
  const refresh = useLabRunStore((s) => s.refresh)

  useEffect(() => {
    startPolling()
    return () => {
      stopPolling()
    }
  }, [startPolling, stopPolling])

  return (
    <div
      className="min-h-[calc(100vh-96px)] text-slate-100"
      data-testid="lab-page"
    >
      <div className="max-w-6xl mx-auto px-6 py-6">
        <header className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">
              Test Lab
            </div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">Past runs</h1>
          </div>
          <button
            type="button"
            data-testid="lab-new-run"
            disabled
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
            title="The /lab/new wizard lands in the next T10 follow-up."
          >
            + New run
          </button>
        </header>

        {disabled ? (
          <DisabledNotice />
        ) : error ? (
          <div
            data-testid="lab-error"
            className="rounded-md border border-red-700/50 bg-red-950/40 p-3 text-sm text-red-200 mb-4"
          >
            {error}
            <button
              type="button"
              onClick={() => void refresh()}
              className="ml-3 underline text-red-100 hover:text-white"
            >
              retry
            </button>
          </div>
        ) : null}

        <section
          data-testid="lab-runs-table"
          className="rounded-2xl border border-slate-700/60 bg-slate-900/60 p-5"
        >
          {!disabled && runs.length === 0 ? (
            <EmptyState loading={loading} />
          ) : (
            <RunsTable runs={runs} />
          )}
        </section>
      </div>
    </div>
  )
}

function DisabledNotice() {
  return (
    <div
      data-testid="lab-disabled"
      className="rounded-md border border-slate-700/60 bg-slate-900/40 p-4 text-sm text-slate-300 mb-4"
    >
      <div className="font-semibold text-slate-100 mb-1">
        Test Lab is not enabled on this server.
      </div>
      <p className="text-xs text-slate-400 leading-relaxed">
        Set <code className="font-mono text-slate-200">AI_ALM_TEST_LAB_ENABLED=1</code>{' '}
        on the backend and restart to expose <code className="font-mono">/api/test-runs</code>.
        Default-OFF in prod images so a public deploy never accidentally exposes
        the orchestrator.
      </p>
    </div>
  )
}

function EmptyState({ loading }: { loading: boolean }) {
  return (
    <div data-testid="lab-empty" className="text-sm text-slate-400">
      {loading
        ? 'Loading runs…'
        : 'No runs yet. Click "+ New run" to launch one once the wizard ships.'}
    </div>
  )
}

function RunsTable({ runs }: { runs: LabRunSummary[] }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-slate-500">
          <th className="text-left py-2 pr-3">Started</th>
          <th className="text-left py-2 pr-3">Persona</th>
          <th className="text-left py-2 pr-3">Scenario</th>
          <th className="text-left py-2 pr-3">Mode</th>
          <th className="text-left py-2 pr-3">Status</th>
          <th className="text-right py-2 pr-3">Duration</th>
          <th className="py-2 pr-3" />
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => (
          <RunRow key={run.id} run={run} />
        ))}
      </tbody>
    </table>
  )
}

function RunRow({ run }: { run: LabRunSummary }) {
  const dur = durationSeconds(run)
  return (
    <tr
      data-testid={`lab-run-${run.id}`}
      data-status={run.status}
      className="border-t border-slate-800/60"
    >
      <td className="py-2 pr-3 text-[11px] text-slate-400 tabular-nums">
        {formatStarted(run.started_at)}
      </td>
      <td className="py-2 pr-3 text-slate-100">{run.persona}</td>
      <td className="py-2 pr-3 text-slate-100">{run.scenario}</td>
      <td className="py-2 pr-3 text-slate-300">{run.mode}</td>
      <td className="py-2 pr-3">
        <StatusBadge status={run.status} />
      </td>
      <td className="py-2 pr-3 text-right text-[11px] text-slate-400 tabular-nums">
        {dur === null ? '—' : `${dur}s`}
      </td>
      <td className="py-2 pr-3 text-right">
        <Link
          data-testid={`lab-run-${run.id}-open`}
          to={`/lab/runs/${encodeURIComponent(run.id)}`}
          className="text-[11px] text-indigo-300 hover:text-indigo-200"
        >
          Open ↗
        </Link>
      </td>
    </tr>
  )
}

function StatusBadge({ status }: { status: LabRunSummary['status'] }) {
  const variant = (() => {
    switch (status) {
      case 'passed':
        return 'alm-chip-ok'
      case 'failed':
        return 'alm-chip-bad'
      case 'stopped':
        return 'alm-chip-warn'
      case 'running':
      case 'queued':
      default:
        return 'alm-chip-info'
    }
  })()
  return (
    <span
      data-testid={`lab-status-${status}`}
      className={`alm-chip ${variant}`}
    >
      {status}
    </span>
  )
}

function formatStarted(iso: string): string {
  const dt = new Date(iso)
  if (Number.isNaN(dt.getTime())) return iso
  return dt.toISOString().slice(0, 19).replace('T', ' ')
}
